/* =============================================================================
 * CCR Chat — AI sales assistant widget.
 * Follows CCR patterns: IIFE, window.CCR namespace, pub/sub events, no deps.
 *
 * Talks to the Cloudflare Worker at /api/chat (URL comes from the runtime
 * config island — data/chatbot.json → api_url). The worker runs the model
 * and its tools (lead capture, booking button, catalog search) server-side
 * and streams a simple SSE protocol back:
 *   {type:"text",  text}          — assistant text delta
 *   {type:"lead",  lead:{...}}    — a lead was captured server-side
 *   {type:"cta",   url, label}    — show a booking button
 *   {type:"error", message}       — user-facing error
 *   {type:"done"}                 — turn complete
 * ========================================================================== */
(function () {
  'use strict';
  var CCR = window.CCR || (window.CCR = {});

  var DEFAULT_CHAT_API = '/api/chat';
  var MAX_HISTORY = 40;

  var els = {};
  var messages = [];     // conversation history sent to the API (user/assistant text)
  var leadData = {};     // populated from server "lead" events (never parsed locally)
  var isOpen = false;
  var isStreaming = false;
  var abortController = null;
  var previousFocus = null;

  /* ---------- config from runtime data island ----------------------------- */
  function getConfig() {
    var runtime = null;
    try {
      var el = document.getElementById('ccr-runtime-config');
      if (el) runtime = JSON.parse(el.textContent);
    } catch (e) {}
    return (runtime && runtime.chatbot) || {};
  }

  function chatApiUrl() {
    var cfg = getConfig();
    return cfg.api_url || DEFAULT_CHAT_API;
  }

  /* ---------- DOM setup --------------------------------------------------- */
  function cacheDom() {
    var root = document.querySelector('[data-ccr-chat]');
    if (!root) return false;
    els.root = root;
    els.toggleBtns = root.querySelectorAll('[data-ccr-chat-toggle]');
    els.window = root.querySelector('#ccr-chat-window');
    els.messages = root.querySelector('[data-ccr-chat-messages]');
    els.form = root.querySelector('[data-ccr-chat-form]');
    els.input = root.querySelector('[data-ccr-chat-input]');
    els.send = root.querySelector('[data-ccr-chat-send]');
    return true;
  }

  /* ---------- toggle open/close ------------------------------------------ */
  function toggle() {
    isOpen = !isOpen;
    els.window.hidden = !isOpen;
    els.root.classList.toggle('ccr-chat--open', isOpen);
    els.toggleBtns.forEach(function (btn) {
      btn.setAttribute('aria-expanded', String(isOpen));
    });
    if (isOpen) {
      previousFocus = document.activeElement;
      if (els.messages.querySelectorAll('.ccr-chat__msg').length === 0) showWelcome();
      els.input.focus();
      scrollToBottom();
      CCR.track('chat_open', {});
    } else {
      // return focus to the launcher for keyboard users
      if (previousFocus && previousFocus.focus) previousFocus.focus();
      else if (els.toggleBtns[0]) els.toggleBtns[0].focus();
    }
  }

  /* ---------- welcome message -------------------------------------------- */
  function showWelcome() {
    var cfg = getConfig();
    var text = (cfg.welcome_message) ||
      'Hi there — I\'m the Theory Coffee assistant. How can I help you today?';
    // skipHistory: the welcome is UI-only. The Messages API requires the
    // first history entry to be a user turn.
    appendMessage('assistant', text, true);
  }

  /* ---------- message rendering ------------------------------------------ */
  function appendMessage(role, content, skipHistory) {
    var div = document.createElement('div');
    div.className = 'ccr-chat__msg ccr-chat__msg--' + role;
    div.innerHTML = '<div class="ccr-chat__msg-text"></div>';
    div.querySelector('.ccr-chat__msg-text').textContent = content;

    els.messages.appendChild(div);
    scrollToBottom();
    if (!skipHistory) {
      messages.push({ role: role, content: content });
      trimHistory();
    }
    return div;
  }

  function trimHistory() {
    if (messages.length > MAX_HISTORY) messages = messages.slice(-MAX_HISTORY);
    // history sent to the API must start with a user turn
    while (messages.length && messages[0].role !== 'user') messages.shift();
  }

  function createStreamingMessage() {
    var div = document.createElement('div');
    div.className = 'ccr-chat__msg ccr-chat__msg--assistant ccr-chat__msg--streaming';
    div.innerHTML = '<div class="ccr-chat__msg-text"></div>';
    els.messages.appendChild(div);
    scrollToBottom();
    return div;
  }

  function showTyping() {
    var div = document.createElement('div');
    div.className = 'ccr-chat__typing';
    div.id = 'ccr-chat-typing';
    div.setAttribute('aria-label', 'Assistant is typing');
    div.innerHTML = '<span></span><span></span><span></span>';
    els.messages.appendChild(div);
    scrollToBottom();
    return div;
  }

  function hideTyping() {
    var el = document.getElementById('ccr-chat-typing');
    if (el) el.remove();
  }

  function scrollToBottom() {
    els.messages.scrollTop = els.messages.scrollHeight;
  }

  /* ---------- booking CTA -------------------------------------------------- */
  function showBookingButton(url, label) {
    if (!url || String(url).indexOf('https:') !== 0) return;
    var btn = document.createElement('a');
    btn.href = url;
    btn.target = '_blank';
    btn.rel = 'noopener noreferrer';
    btn.className = 'ccr-chat__booking-btn';
    btn.textContent = label || 'Book a sales call';
    btn.addEventListener('click', function () {
      CCR.track('chat_booking_click', {
        has_lead_name: !!leadData.name,
        has_lead_email: !!leadData.email,
        has_lead_company: !!leadData.company
      });
    });
    els.messages.appendChild(btn);
    scrollToBottom();
  }

  /* ---------- send message & stream response ----------------------------- */
  function sendMessage(text) {
    if (!text.trim() || isStreaming) return;

    appendMessage('user', text.trim());

    els.input.value = '';
    els.input.style.height = 'auto';
    els.send.disabled = true;
    isStreaming = true;

    CCR.track('chat_message', {
      role: 'user',
      message_index: messages.length,
      has_lead_email: !!leadData.email
    });

    showTyping();
    abortController = new AbortController();
    streamResponse();
  }

  // Attribution sent with every chat request so the worker can stamp the
  // visitor's experiment variant assignments onto the Shopify customer at
  // lead capture — the basis for attributing off-session booked appointments
  // back to the landing-page variant that produced them.
  function attribution() {
    var experiments = {};
    try {
      if (CCR.experiments && CCR.experiments.activeAssignments) {
        experiments = CCR.experiments.activeAssignments();
      }
    } catch (e) {}
    return {
      client_id: CCR.clientId || '',
      experiments: experiments,
      utm: CCR.utm || {},
      landing: window.location.pathname
    };
  }

  function chatTurnstileHost() {
    return els.root ? els.root.querySelector('[data-ccr-chat-turnstile]') : null;
  }

  function obtainChatTurnstileToken() {
    var host = chatTurnstileHost();
    if (!CCR.turnstile || !host || !CCR.turnstile.siteKey()) {
      return Promise.resolve('');
    }
    CCR.turnstile.reset(host);
    return CCR.turnstile.getToken(host, { size: 'invisible', execute: true });
  }

  function streamResponse() {
    var cfg = getConfig();
    var attr = attribution();

    obtainChatTurnstileToken().then(function (turnstileToken) {
      if (!turnstileToken) {
        throw new Error('Security check failed. Please refresh and try again.');
      }
      return fetch(chatApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: messages,
          client_id: attr.client_id,
          experiments: attr.experiments,
          utm: attr.utm,
          landing: attr.landing,
          turnstile_token: turnstileToken
        }),
        signal: abortController.signal
      });
    })
    .then(function (response) {
      if (!response.ok) {
        return response.json().then(function (err) {
          throw new Error(err.error || 'Request failed');
        }, function () {
          throw new Error('Request failed');
        });
      }

      var reader = response.body.getReader();
      var decoder = new TextDecoder();
      var buffer = '';
      var msgDiv = null;
      var textEl = null;
      var fullText = '';

      function ensureMessageEl() {
        if (!msgDiv) {
          hideTyping();
          msgDiv = createStreamingMessage();
          textEl = msgDiv.querySelector('.ccr-chat__msg-text');
        }
      }

      function handleEvent(evt) {
        if (evt.type === 'text') {
          ensureMessageEl();
          fullText += evt.text;
          textEl.textContent = fullText;
          scrollToBottom();
        } else if (evt.type === 'lead') {
          leadData = evt.lead || {};
          CCR.emit('chat:lead_update', leadData);
          CCR.track('chat_lead_saved', {
            has_name: !!leadData.name,
            has_email: !!leadData.email,
            has_company: !!leadData.company
          });
        } else if (evt.type === 'cta') {
          // finish the current bubble first so the button lands after it
          finishBubble();
          showBookingButton(evt.url, evt.label);
        } else if (evt.type === 'error') {
          hideTyping();
          appendMessage('assistant', evt.message ||
            (cfg.error_message || 'I\'m having trouble right now. Please try again in a moment.'), true);
        }
      }

      function finishBubble() {
        if (msgDiv) {
          msgDiv.classList.remove('ccr-chat__msg--streaming');
          if (fullText.trim()) {
            messages.push({ role: 'assistant', content: fullText });
            trimHistory();
          }
          CCR.track('chat_message', {
            role: 'assistant',
            message_index: messages.length
          });
          msgDiv = null;
          textEl = null;
          fullText = '';
        }
      }

      function finishTurn() {
        hideTyping();
        finishBubble();
        isStreaming = false;
        els.send.disabled = !els.input.value.trim();
      }

      function pump() {
        return reader.read().then(function (result) {
          if (result.done) { finishTurn(); return; }
          buffer += decoder.decode(result.value, { stream: true });
          var lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (var i = 0; i < lines.length; i++) {
            var line = lines[i].trim();
            if (!line.startsWith('data: ')) continue;
            var evt;
            try { evt = JSON.parse(line.slice(6)); } catch (e) { continue; }
            if (evt.type === 'done') { finishTurn(); return; }
            handleEvent(evt);
          }
          return pump();
        });
      }
      return pump();
    })
    .catch(function (err) {
      hideTyping();
      isStreaming = false;
      els.send.disabled = !els.input.value.trim();
      if (err.name !== 'AbortError') {
        appendMessage('assistant', err.message && err.message !== 'Request failed'
          ? err.message
          : (cfg.error_message || 'I\'m having trouble connecting right now. Please try again in a moment.'), true);
      }
    });
  }

  /* ---------- textarea auto-resize --------------------------------------- */
  function autoResize() {
    els.input.style.height = 'auto';
    els.input.style.height = Math.min(els.input.scrollHeight, 120) + 'px';
    els.send.disabled = !els.input.value.trim() || isStreaming;
  }

  /* ---------- keyboard handling ------------------------------------------ */
  function handleKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isStreaming && els.input.value.trim()) {
        sendMessage(els.input.value);
      }
    }
  }

  function handleGlobalKeydown(e) {
    if (e.key === 'Escape' && isOpen) {
      toggle();
      return;
    }
    if (e.key === 'Tab' && isOpen) {
      trapFocus(e);
    }
  }

  function focusableElements() {
    return Array.prototype.slice.call(els.window.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) {
      return el.offsetParent !== null;
    });
  }

  function trapFocus(e) {
    var items = focusableElements();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---------- init -------------------------------------------------------- */
  function init() {
    if (!cacheDom()) return;

    els.toggleBtns.forEach(function (btn) {
      btn.addEventListener('click', toggle);
    });

    els.form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!isStreaming && els.input.value.trim()) {
        sendMessage(els.input.value);
      }
    });

    els.input.addEventListener('input', autoResize);
    els.input.addEventListener('keydown', handleKeydown);
    document.addEventListener('keydown', handleGlobalKeydown);

    CCR.chat = {
      open: function () { if (!isOpen) toggle(); },
      close: function () { if (isOpen) toggle(); },
      isOpen: function () { return isOpen; },
      leadData: function () { return Object.assign({}, leadData); }
    };
  }

  CCR.ready(init);
})();
